import type { ActivityKey, ActivitySections } from '../activity-journal';
import {
    type ActivityBuildingDisplay,
    unresolvedActivityBuildingLabel,
} from './building-display';
import { toActivityAction } from './card-policy';
import { mapActivityPanelGroups } from './mapper';
import type {
    ActivityBuildingGroupView,
    ActivityOutcomeProjection,
    ActivityOutcomeToastInput,
    ActivityOutcomeToastView,
    ActivityPanelItem,
} from './models';

const EMPTY_BUILDING_LOOKUPS: ReadonlyMap<string, ActivityBuildingDisplay> =
    new Map();

export function projectActivityOutcomes(
    sections: ActivitySections,
    now = Date.now(),
    buildingLookups = EMPTY_BUILDING_LOOKUPS
): ActivityOutcomeProjection {
    const groups = mapActivityPanelGroups(sections, now, buildingLookups);
    const itemsByKey = indexPanelItems(groups);

    return {
        groups,
        toastFor: (input) =>
            toActivityOutcomeToast(input, itemsByKey, buildingLookups),
    };
}

function indexPanelItems(
    groups: readonly ActivityBuildingGroupView[]
): ReadonlyMap<ActivityKey, ActivityPanelItem> {
    const itemsByKey = new Map<ActivityKey, ActivityPanelItem>();
    for (const item of groups.flatMap((group) => group.items)) {
        itemsByKey.set(item.id, item);
        if (item.kind === 'document-batch') {
            for (const itemId of item.itemIds) itemsByKey.set(itemId, item);
        }
    }
    return itemsByKey;
}

function toActivityOutcomeToast(
    input: ActivityOutcomeToastInput,
    itemsByKey: ReadonlyMap<ActivityKey, ActivityPanelItem>,
    buildingLookups: ReadonlyMap<string, ActivityBuildingDisplay>
): ActivityOutcomeToastView {
    const item = itemsByKey.get(input.key);
    const buildingLabel =
        item?.buildingLabel ??
        activityBuildingLabel(input.buildingPublicId, buildingLookups);
    const title =
        item?.title === 'Documents imported'
            ? 'Documents imported successfully'
            : (item?.title ?? 'Background document work');

    return {
        key: input.key,
        kind: input.kind === 'failure' ? 'error' : input.kind,
        title,
        buildingLabel,
        description: input.statusMessage
            ? `${buildingLabel} · ${input.statusMessage}`
            : buildingLabel,
        action: toActivityAction(input.intent, buildingLabel),
    };
}

function activityBuildingLabel(
    buildingPublicId: string | null,
    buildingLookups: ReadonlyMap<string, ActivityBuildingDisplay>
): string {
    if (!buildingPublicId) return 'Unidentified building';
    return (
        buildingLookups.get(buildingPublicId)?.label ??
        unresolvedActivityBuildingLabel(buildingPublicId)
    );
}
